import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Users, ChevronRight, BookOpen } from "lucide-react";

export const Route = createFileRoute("/teacher/classes")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Classes — MAKAI" }] }),
  component: ClassesPage,
});

type Learner = { id: string; first_name: string; last_name: string; admission_number: string | null; photo_url: string | null };

function ClassesPage() {
  const ctx = useTeacherContext();
  const [selected, setSelected] = useState<string | null>(null);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!selected && ctx.streams.length) setSelected(ctx.streams[0].id);
  }, [ctx.streams, selected]);

  useEffect(() => {
    if (!selected) { setLearners([]); return; }
    supabase
      .from("learners")
      .select("id, first_name, last_name, admission_number, photo_url")
      .eq("current_stream_id", selected).eq("status", "active")
      .order("first_name")
      .then(({ data }) => setLearners((data ?? []) as Learner[]));
  }, [selected]);

  const filtered = useMemo(() => {
    if (!q.trim()) return learners;
    const s = q.toLowerCase();
    return learners.filter((l) =>
      `${l.first_name} ${l.last_name}`.toLowerCase().includes(s)
      || (l.admission_number?.toLowerCase().includes(s) ?? false));
  }, [learners, q]);

  const stream = ctx.streams.find((s) => s.id === selected);
  const subjects = ctx.assignments.filter((a) => a.stream.id === selected && a.subject).map((a) => a.subject!);

  return (
    <AppShell role="teacher" title="My Classes" subtitle="Classes and subjects you are assigned to teach.">
      {ctx.loading ? (
        <div className="grid h-64 place-items-center text-muted-foreground">Loading…</div>
      ) : ctx.streams.length === 0 ? (
        <EmptyShell />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="space-y-2">
            {ctx.streams.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`w-full rounded-2xl border p-4 text-left transition ${
                  selected === c.id ? "border-primary bg-primary-soft" : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold">{c.class_name}</div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">Stream {c.name}</div>
              </button>
            ))}
          </aside>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
              <div className="min-w-0">
                <h2 className="truncate font-display text-xl font-bold">{stream?.class_name} · {stream?.name}</h2>
                <p className="text-xs text-muted-foreground">{learners.length} learners</p>
              </div>
            </div>

            {subjects.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {subjects.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">
                    <BookOpen className="h-3 w-3" /> {s.name}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search learners by name or admission #" className="pl-9" />
              </div>
              <Button variant="outline" size="sm" className="hidden sm:inline-flex"><Users className="mr-1 h-4 w-4" /> {filtered.length} shown</Button>
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Learner</th>
                    <th className="hidden px-4 py-2 sm:table-cell">Admission #</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={2} className="px-4 py-8 text-center text-muted-foreground">No learners found.</td></tr>
                  ) : filtered.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-gold text-xs font-bold text-gold-foreground">
                            {r.first_name[0]}{r.last_name[0]}
                          </div>
                          <span className="font-medium">{r.first_name} {r.last_name}</span>
                        </div>
                      </td>
                      <td className="hidden px-4 py-3 text-xs text-muted-foreground sm:table-cell">{r.admission_number ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </AppShell>
  );
}

function EmptyShell() {
  return (
    <div className="rounded-2xl border border-dashed border-border p-10 text-center">
      <h3 className="font-display text-lg font-bold">No classes assigned yet</h3>
      <p className="mt-1 text-sm text-muted-foreground">Ask your school admin to assign you to classes and subjects.</p>
    </div>
  );
}
